import {
	IconButton,
	InputRow,
	SecretValueInput,
	TextInput,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { ReactElement, useCallback } from "react";
import styles from "./PossibleSecretKeyValueCollection.module.less";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import {
	KeyedValueSaveModel,
	SecretSaveModel,
	StringOrSecretSaveModel,
	StringSaveModel,
	StringSecretDiscriminator
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";

export type PossibleSecretKeyValueCollectionProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<KeyedValueSaveModel>[]>;
	maxAmount: number;
};

export const PossibleSecretKeyValueCollection = (props: PossibleSecretKeyValueCollectionProps): ReactElement => {
	const {
		attachable,
		maxAmount
	} = props;

	const addHandler = useCallback(
		(): void => {
			const item: KeyedValueSaveModel = {
				id: null,
				key: "",
				value: {
					kind: StringSecretDiscriminator.String,
					string: ""
				}
			};

			attachable.reducer
				.push(setClientId(item));

			attachable.validation
				.triggerInputFinished();
		},
		[attachable.reducer, attachable.validation]
	);

	const removeHandler = useCallback(
		(index: number): void => {
			attachable
				.reducer
				.removeAt(index);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable.reducer, attachable.validation]
	);

	return (
		<div
			{...setTrackingId(attachable.reducer.path)}
			className={styles.root}
		>
			<NoUniqueInputIdContext>
				<ul className={styles.list}>
					{attachable.map((x, i): ReactElement => (
						<li key={getClientId(x.value)}>
							<Item
								attachable={x}
								onRemove={() => removeHandler(i)}
							/>
						</li>
					))}
				</ul>
			</NoUniqueInputIdContext>

			{attachable.value.length < maxAmount && (
				<IconButton
					kind="dashed"
					width="fill"
					icon="plus"
					onClick={addHandler}
					trackingId="add"
				/>
			)}
		</div>
	);
};
PossibleSecretKeyValueCollection.displayName = "PossibleSecretKeyValueCollection";

type ItemProps = {
	attachable: IValueInputAttachable<ObjectWithClientId<KeyedValueSaveModel>>;
	onRemove: () => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		attachable,
		onRemove
	} = props;

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const turnToSecretHandler = useCallback(
		async (valueAttachable: IValueInputAttachable<StringOrSecretSaveModel>): Promise<void> => {
			const { value } = valueAttachable;
			if (value.kind !== StringSecretDiscriminator.String) {
				return;
			}

			const confirmationResult: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToTurnThisValueIntoASecret"),
				children: localize("_TurnIntoSecretExplanation"),
				confirmText: localize("_TurnIntoSecret"),
				trackingId: "hide-secret-value-confirmation-dialog"
			});

			if (!confirmationResult.wasSuccessful) {
				return;
			}

			const newValue: SecretSaveModel = {
				kind: StringSecretDiscriminator.Secret,
				secret: {
					containsNewValue: true,
					value: value.string
				}
			};

			valueAttachable.reducer
				.setValue(newValue);

			valueAttachable.validation
				.triggerInputFinished();
		},
		[openConfirmationDialog]
	);

	return (
		<>
			<div
				{...setTrackingId(attachable.reducer.path)}
				className={styles.item}
			>
				<div className={styles.input}>
					<TextInput
						{...attachable.for("key").attachToTextInput()}
						placeholder={localize("_EnterKey")}
					/>

					<ValidationError {...attachable.for("key").attachToValidationError()} />
				</div>

				<div className={styles.input}>
					{attachable.for("value")
						.whenType(
							(x): x is StringSaveModel => x.kind === StringSecretDiscriminator.String,
							(x) => {
								return (
									<InputRow>
										<InputRow.Stretch>
											<TextInput
												{...x.for("string").attachToTextInput()}
												placeholder={localize("_EnterValue")}
											/>
										</InputRow.Stretch>

										<IconButton
											kind="secondary"
											icon="lock"
											tooltip={localize("_TurnIntoSecret")}
											trackingId="turn-to-secret"
											onClick={() => turnToSecretHandler(x)}
										/>
									</InputRow>
								);
							},
							(x) => {
								return (
									<SecretValueInput
										{...x.for("secret").attachToSecretValueInput()}
										placeholder={localize("_EnterValue")}
										containsInitialValue={isNotNil(attachable.value.id)}
									/>
								);
							}
						)}

					<ValidationError {...attachable.for("value").attachToValidationError()} />
				</div>

				<IconButton
					kind="secondary"
					icon="minus"
					onClick={onRemove}
					trackingId="remove"
				/>
			</div>

			{confirmationDialog}
		</>
	);
};
Item.displayName = "PossibleSecretKeyValueCollection.Item";
